import cx from "classnames";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import QESOptionsList, { OptionsListOption } from "@app/shared/qes-standards/forms/OptionsList";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FormFieldStates, InputProps } from "@app/shared/qes-standards/forms/exports";
import { JSX, ChangeEvent, FC, InputHTMLAttributes, PropsWithChildren, RefObject, useMemo, useRef, useState } from "react";
import { TextInputContext } from "./textInputContext";

export type QESTextInputProps = InputProps & InputHTMLAttributes<HTMLInputElement> & TextInputProps;

interface TextInputProps {
  optionsListConfig?: OptionsListConfig;
  inputPrepend?: JSX.Element;
}

interface OptionsListConfig {
  options: OptionsListOption[];
  showUnmatched?: boolean;
  noMatchText?: string;
  onOptionClick?: (option: OptionsListOption) => void;
  matchFunction?: (option: OptionsListOption, inputValue: string) => boolean;
  minChars?: number;
  maxResults?: number;
}

/**
 * A standard text field for easy reuse. It requires a label above, has an optional descriptive snippet below, and accepts all the same props as a native HTML input element.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESTextInput: FC<PropsWithChildren<QESTextInputProps>> = (props) => {
  const [focused, setFocused] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(props.wasFocusedExternally);
  const [optionsListOpen, setOptionsListOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);

  const {
    containerClassName,
    descriptiveText,
    descriptiveTextClassname,
    disabled,
    errorText,
    hideLabel,
    inlineLabel,
    inputAppend,
    inputPrepend,
    isValid,
    label,
    labelWidth,
    optionsListConfig,
    qesRef,
    required,
    testId,
    type,
    unrequiredValidation,
    validationTooltipContent,
    wasFocusedExternally,
    ...restProps
  } = props;

  const optionsList = optionsListConfig?.options;
  const optionsNoMatchText = optionsListConfig?.noMatchText;
  const optionsShowUnmatched = optionsListConfig?.showUnmatched;
  const optionsMatchedFunction = optionsListConfig?.matchFunction;
  const optionsMinChars = optionsListConfig?.minChars ?? 0;
  const optionsMaxResults = optionsListConfig?.maxResults ?? Infinity;

  const meetsMinChars = ((props.value?.toString().trim().length) ?? 0) >= optionsMinChars;

  const activeMatchFunction = useMemo(() => optionsMatchedFunction || ((option: OptionsListOption, inputValue: string) => option.value.toLowerCase().includes(inputValue.toLowerCase())), [optionsMatchedFunction]);

  const fieldState = useMemo(() => {
    if (!isValid && (required || unrequiredValidation) && (hasBeenFocused || focused || wasFocusedExternally)) {
      return FormFieldStates.Error;
    }
    return FormFieldStates.None;
  }, [isValid, required, unrequiredValidation, hasBeenFocused, focused, wasFocusedExternally]);

  const subText = useMemo(() => {
    if ((hasBeenFocused || wasFocusedExternally) && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused, wasFocusedExternally]);

  const handleOnBlur = () => {
    if (!hasBeenFocused) {
      setHasBeenFocused(true);
    }

    setFocused(false);
    setOptionsListOpen(false);
    setHighlightedIndex(-1);
  }

  const handleOnClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (props.onClick) {
      props.onClick(e);
    }

    setOptionsListOpen(true);
    setHighlightedIndex(-1);
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!!props.onChange) {
      props.onChange(e);
    }

    if (optionsList && optionsList.length > 0) {
      setOptionsListOpen(true);
      setHighlightedIndex(-1);
    }
  }

  const matchedOptions = useMemo(() => {
    if (!optionsList || !meetsMinChars) {
      return [];
    }

    if (!props.value) {
      return optionsList.slice(0, optionsMaxResults);
    }

    const filteredOptions = optionsList.filter(option => activeMatchFunction(option, props.value.toString()));
    filteredOptions.sort();
    return filteredOptions.slice(0, optionsMaxResults);
  }, [activeMatchFunction, meetsMinChars, optionsList, optionsMaxResults, props.value]);

  const nonMatchedOptions = useMemo(() => {
    if (!props.value || !optionsList || !meetsMinChars) {
      return [];
    }

    const filteredOptions = optionsList.filter(option => !activeMatchFunction(option, props.value.toString()));
    filteredOptions.sort();
    return filteredOptions;
  }, [activeMatchFunction, meetsMinChars, optionsList, props.value]);

  const allDisplayedOptions = useMemo(() => {
    if (!optionsList) {
      return [];
    }

    if (!optionsShowUnmatched) {
      return matchedOptions;
    }

    return [...matchedOptions, ...nonMatchedOptions];
  }, [optionsList, matchedOptions, nonMatchedOptions, optionsShowUnmatched]);

  const handleOptionsListScroll = (newHighlightedIndex: number) => {
    if (!optionsListRef.current) {
      return;
    }
    const buttons = optionsListRef.current.querySelectorAll('button') as NodeListOf<HTMLButtonElement>;
    if (!buttons || buttons.length <= newHighlightedIndex || newHighlightedIndex < 0) {
      return;
    }
    const buttonToSelect = buttons[newHighlightedIndex];
    buttonToSelect.scrollIntoView({ block: "nearest" });
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }

    if (!optionsList) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!optionsListOpen) {
          setOptionsListOpen(true);
          setHighlightedIndex(0);
        } else {
          const newHighlightedIndex = highlightedIndex < (allDisplayedOptions.length - 1) ? highlightedIndex + 1 : highlightedIndex;
          setHighlightedIndex(newHighlightedIndex);
          handleOptionsListScroll(newHighlightedIndex);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (optionsListOpen) {
          const newHighlightedIndex = highlightedIndex > 0 ? highlightedIndex - 1 : highlightedIndex;
          setHighlightedIndex(newHighlightedIndex);
          handleOptionsListScroll(newHighlightedIndex);
        }
        break;

      case 'Escape':
        if (optionsListOpen) {
          e.preventDefault();
          e.stopPropagation();
          setOptionsListOpen(false);
          setHighlightedIndex(-1);
        }
        break;

      case 'Enter':
        if (optionsListOpen && highlightedIndex >= 0 && highlightedIndex < allDisplayedOptions.length) {
          e.preventDefault();

          const selectedOption = allDisplayedOptions[highlightedIndex];
          props.onChange({ target: { value: selectedOption.value } } as ChangeEvent<HTMLInputElement>);

          if (optionsListConfig?.onOptionClick) {
            optionsListConfig.onOptionClick(selectedOption);
          }

          setOptionsListOpen(false);
          setHighlightedIndex(-1);
        }
        break;
    }
  }

  const characterCount = useMemo(() => {
    return (props.value as string)?.length || 0;
  }, [props.value]);

  const isShowingError = (hasBeenFocused || wasFocusedExternally) && fieldState === FormFieldStates.Error;

  return (
    <div
      className={cx({
        [containerClassName]: !!containerClassName,
        "inline-label": inlineLabel,
        "qes-text-input__container": true,
      })}
      key={testId}
      onBlur={() => handleOnBlur()}
      ref={containerRef}
    >
      <label
        htmlFor={props.id || testId}
        className={cx({
          "qes-text-input__label": true,
          "visually-hidden-label": hideLabel,
        })}
        style={{ width: labelWidth ?? "auto" }}
      >
        {label}
        {required || validationTooltipContent ? (
          <span className="qes-text-input__label-subcontent">
            {required ? <span className="text-red500"> *</span> : ""}
            {validationTooltipContent ? <TooltipWrapper containerClassName="qes-text-input__tooltip-container" text={validationTooltipContent}><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="16" /></TooltipWrapper> : ""}
          </span>
        ) : null}
      </label>
      <div className="qes-text-input__subcontainer">
        <div
          className={cx({
            "disabled": disabled,
            "error": isShowingError,
            "focus": focused,
            [props.className]: !!props.className,
            "qes-text-input__group": true,
            "qes-text-input__options-container": !!optionsListConfig,
          })}
        >
          {!!inputPrepend && (
            <span className="qes-text-input__append qes-text-input__prepend">
              {inputPrepend}
            </span>
          )}
          <input
            {...restProps}
            disabled={disabled}
            id={props.id || testId}
            type={type ?? 'text'}
            autoComplete={!!optionsListConfig ? "off" : "on"}
            className={cx({
              "error": isShowingError,
              "qes-text-input__input": true,
            })}
            onChange={handleOnChange}
            onClick={handleOnClick}
            onFocus={() => setFocused(true)}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
            ref={qesRef as RefObject<HTMLInputElement>}
            required={required}
            data-testid={testId}
          />

          {!!inputAppend && (
            <span className="qes-text-input__append">
              {inputAppend}
            </span>
          )}
          {!!optionsList &&
            <TextInputContext.Provider value={{
              focused,
              highlightedIndex,
              onChange: props.onChange,
              optionsListOpen: optionsListOpen && meetsMinChars,
              setFocused,
            }}>
              <QESOptionsList
                matchedOptions={matchedOptions}
                nonMatchedOptions={nonMatchedOptions}
                onOptionClick={optionsListConfig?.onOptionClick}
                optionsListRef={optionsListRef}
                textIfNoMatch={optionsNoMatchText ?? "No options found."}
                showUnmatched={optionsShowUnmatched} />
            </TextInputContext.Provider>
          }
        </div>
        <div>
          {subText && (
            <small className={`qes-text-input__descriptive-text${!!descriptiveTextClassname ? ` ${descriptiveTextClassname}` : ""}`}>
              {isShowingError && <QesIcon iconName={QesIcons.XCircle} isDecorative />}
              {subText}
            </small>
          )}
          {props.maxLength && (
            <small
              className={cx({
                "danger": characterCount >= props.maxLength,
                "qes-text-input__descriptive-text qes-text-input__character-count": true,
              })}
            >
              {(props.showCounter === true || props.showCounter === undefined) && (
                <>
                  {(props.maxLength - characterCount).toLocaleString()} / {props.maxLength.toLocaleString()}
                </>
              )}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};
